import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { AnimatedPyramidLogo } from '@/components/landing/AnimatedLedgerLoop';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { logger } from '@/lib/logger';
import { toast } from 'sonner';

const AuthVerify = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const verifyMagicLink = async () => {
      try {
        // Get token from query params (not hash params)
        const queryParams = new URLSearchParams(window.location.search);
        const token = queryParams.get('token');
        const isAdminAccess = queryParams.get('admin') === 'true';

        if (!token) {
          throw new Error('Invalid magic link - no token provided');
        }

        // Call our custom verification edge function
        const { data, error } = await supabase.functions.invoke('verify-magic-link', {
          body: { token },
        });

        if (error) throw error;
        if (!data?.success) throw new Error(data?.error || 'Failed to verify magic link');

        // Set the session with the tokens from our edge function
        const { data: sessionData, error: sessionError } = await supabase.auth.setSession({
          access_token: data.access_token,
          refresh_token: data.refresh_token,
        });

        if (sessionError) throw sessionError;

        logger.info('Magic link verified successfully', {
          meta: { userId: sessionData.user?.id, isAdminAccess },
        });
        logger.audit(isAdminAccess ? 'Admin quick access verified' : 'Magic link verified', {
          meta: { userId: sessionData.user?.id, email: sessionData.user?.email },
        });

        setStatus('success');
        toast.success(isAdminAccess ? 'Admin access granted!' : 'Signed in successfully!');

        // Redirect after brief delay
        setTimeout(() => {
          if (isAdminAccess) {
            // Direct to super admin portal for admin quick access
            navigate('/super-admin');
          } else {
            // Regular login flow - check if user needs onboarding
            // Check both created companies and assigned companies via roles
            Promise.all([
              supabase
                .from('companies')
                .select('id')
                .eq('created_by', sessionData.user!.id)
                .limit(1),
              supabase
                .from('user_roles')
                .select('company_id')
                .eq('user_id', sessionData.user!.id)
                .not('company_id', 'is', null)
                .limit(1),
            ])
              .then(([createdCompanies, assignedCompanies]) => {
                console.log('Created companies:', createdCompanies);
                console.log('Assigned companies:', assignedCompanies);

                if (createdCompanies.error) {
                  console.error('Error fetching created companies:', createdCompanies.error);
                }
                if (assignedCompanies.error) {
                  console.error('Error fetching assigned companies:', assignedCompanies.error);
                }

                const hasCompanyAccess =
                  (createdCompanies.data && createdCompanies.data.length > 0) ||
                  (assignedCompanies.data && assignedCompanies.data.length > 0);

                console.log('Has company access:', hasCompanyAccess);

                if (!hasCompanyAccess) {
                  console.log('Redirecting to onboarding');
                  navigate('/onboarding');
                } else {
                  console.log('Redirecting to dashboard');
                  navigate('/dashboard');
                }
              })
              .catch((error) => {
                console.error('Error checking company access:', error);
                navigate('/onboarding');
              });
          }
        }, 1000);
      } catch (error: any) {
        logger.error('Magic link verification failed', error);
        setStatus('error');
        setErrorMessage(error.message || 'Failed to verify magic link');
        toast.error('Failed to sign in');
      }
    };

    verifyMagicLink();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-4">
          <div className="flex justify-center">
            <AnimatedPyramidLogo variant="auth" />
          </div>
          <CardTitle className="text-2xl font-semibold text-center">
            {status === 'verifying' && 'Verifying Your Link'}
            {status === 'success' && 'Welcome Back!'}
            {status === 'error' && 'Verification Failed'}
          </CardTitle>
          <CardDescription className="text-center">
            {status === 'verifying' && 'Please wait while we sign you in...'}
            {status === 'success' && 'Redirecting you to CapLedger...'}
            {status === 'error' && errorMessage}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col items-center space-y-4">
          {status === 'verifying' && <Loader2 className="h-8 w-8 animate-spin text-primary" />}
          {status === 'success' && (
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Taking you to your dashboard...</p>
            </div>
          )}
          {status === 'error' && (
            <div className="w-full space-y-4">
              <div className="text-sm text-muted-foreground text-center space-y-2">
                <p>This magic link may have expired or already been used.</p>
                <p>Magic links are single-use and expire after 60 minutes.</p>
              </div>
              <Button className="w-full" onClick={() => navigate('/auth')}>
                Request New Magic Link
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AuthVerify;
